/**
 * Chatbot Overlay Component
 * 
 * Displays the chatbot as an overlay/sidebar on all pages
 * Available on all screens including before login
 */

import React, { useEffect, useRef, useState } from "react";
import { ChatbotAssistant } from "./chatbot/ChatbotAssistant";
import "../styles/ChatbotOverlay.scss";

interface ChatbotOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  usageMode?: "authenticated" | "public";
}

type ChatbotSizeMode = "default" | "expanded";

const CHATBOT_SIZE_STORAGE_KEY = "evoly.chatbot.sizeMode";

export const ChatbotOverlay: React.FC<ChatbotOverlayProps> = ({
  isOpen,
  onClose,
  usageMode = "authenticated",
}) => {
  const overlayRef = useRef<HTMLDivElement>(null);
  const [sizeMode, setSizeMode] = useState<ChatbotSizeMode>("default");

  useEffect(() => {
    if (typeof window === "undefined") return;
    const savedSizeMode = window.localStorage.getItem(
      CHATBOT_SIZE_STORAGE_KEY,
    ) as ChatbotSizeMode | null;
    if (savedSizeMode === "default" || savedSizeMode === "expanded") {
      setSizeMode(savedSizeMode);
    }
  }, []);

  const handleToggleSize = () => {
    setSizeMode((prev) => {
      const nextMode: ChatbotSizeMode =
        prev === "default" ? "expanded" : "default";
      if (typeof window !== "undefined") {
        window.localStorage.setItem(CHATBOT_SIZE_STORAGE_KEY, nextMode);
      }
      return nextMode;
    });
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div
      ref={overlayRef}
      className={`chatbot-overlay ${sizeMode === "expanded" ? "chatbot-overlay--expanded" : ""}`}
      role="dialog"
      aria-labelledby="chatbot-header-title"
    >
      <ChatbotAssistant
        onClose={onClose}
        usageMode={usageMode}
        sizeMode={sizeMode}
        onToggleSize={handleToggleSize}
      />
    </div>
  );
};
